import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonController } from '@classes/common.controller';
import { AttachmentService } from '@common/services/attachment.service';
import { CodelistService } from '@common/services/codelist.service';
import { marker } from '@common/translate-marker';
import { requiredValidator } from '@common/validators/required-validator';
import { TranslateService } from '@ngx-translate/core';
import { FileRestrictions } from '@progress/kendo-angular-upload';
import { ChatService } from '@services/chat.service';
import { ModalService } from '@services/modal.service';
import { ToastService } from '@services/toast.service';
import { CommonService } from 'app/singleton-services/common.service';
import { Observable } from 'rxjs';
import { ChatMode } from '../tab-chat.component';

@Component({
    selector: 'app-chat-window-new-note',
    templateUrl: './chat-window-new-note.component.html',
    styleUrls: ['./chat-window-new-note.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatWindowNewNoteComponent extends CommonController implements OnInit {
    @Input() vesselVisitId: number | null = null;
    @Input() serviceRequestId: number | null = null;
    @Input() storageDocumentId: number | null = null;
    @Input() amendmentDocumentId: number | null = null;
    @Input() mode: ChatMode = ChatMode.General;
    @Output() closeModal = new EventEmitter<boolean>();

    form: FormGroup;

    fileToSend: string = null;
    fileName: string = null;

    users$: Observable<any>;
    chatTypes: any[] = [];

    isBusy: boolean = false;

    attachmentFileUploadRestrictions: FileRestrictions = {
        maxFileSize: 5242880,
        allowedExtensions: ['.pdf', '.jpg', '.png', '.csv', '.xls', '.txt', '.xlsx', '.doc', '.docx', '.jpeg', '.gif']
    };

    constructor(
        protected dialogService: ModalService,
        protected translateService: TranslateService,
        private service: ChatService,
        private attachmentService: AttachmentService,
        private toastyService: ToastService,
        private codeListService: CodelistService,
        private commonService: CommonService
    ) {
        super(dialogService, translateService);
    }

    ngOnInit(): void {
        this.isBusy = true;
        this.form = this.createFormGroup();
        this.users$ = this.codeListService.getUsersBasedOnOrganization();
        this.loadAttachmentSettings();
        this.commonService.getDropdownOptions('ChatNoteType').subscribe((options) => {
            if (!options) return;

            // if user doesn't have permission Chat.Chat.ForAll, remove FOR_ALL option
            this.chatTypes = options.filter((option) => {
                if (option.id === 'FOR_ALL' && !this.hasPermission('Chat.Chat.ForAll')) {
                    return false;
                }
                return true;
            });

            this.isBusy = false;
        });
    }

    chatTypeSelectionChange(typeId: string) {
        const orgControl = this.form.get('forOrganizationId');
        const userControl = this.form.get('forUserId');
        const watcherControl = this.form.get('watcherOrganizationId');
        if (!typeId) {
            userControl.patchValue(null);
            orgControl.patchValue(null);
            watcherControl.patchValue(null);
            userControl.disable();
            orgControl.disable();
            return;
        }

        if (typeId === 'FOR_ALL') {
            orgControl.disable();
            userControl.disable();
            orgControl.patchValue(null);
            userControl.patchValue(null);
            watcherControl.patchValue(null);
        } else if (typeId === 'INT_FOR_ME') {
            orgControl.disable();
            userControl.disable();
            orgControl.patchValue(this.user.organization.id);
            userControl.patchValue(this.user.id);
            watcherControl.patchValue(null);
        } else if (typeId === 'INT_FOR_MY_ORG') {
            orgControl.disable();
            userControl.disable();
            orgControl.patchValue(this.user.organization.id);
            userControl.patchValue(null);
            watcherControl.patchValue(null);
        } else if (typeId === 'FOR_ORG') {
            orgControl.enable();
            userControl.disable();
            userControl.patchValue(null);
            orgControl.patchValue(null);
        } else if (typeId === 'FOR_USER') {
            userControl.patchValue(null);
            orgControl.patchValue(null);
            userControl.enable();
            orgControl.enable();
            watcherControl.patchValue(null);
        }
    }

    organizationSelectionChange(orgId: number) {
        const userControl = this.form.get('forUserId');
        if (!orgId) {
            userControl.disable();
            userControl.patchValue(null);
            return;
        }

        if (this.form.get('typeId').value === 'FOR_USER') {
            userControl.enable();
            this.users$ = this.codeListService.getUsersBasedOnOrganization(orgId);
        }
    }

    submitNewChatNot() {
        const options = this.form.getRawValue();
        options.file = this.fileToSend;
        options.fileName = this.fileName;

        if (this.mode === ChatMode.VesselVisit) options['vesselVisitId'] = this.vesselVisitId;
        else if (this.mode === ChatMode.ServiceRequest) options['serviceRequestId'] = this.serviceRequestId;
        else if (this.mode === ChatMode.StorageDocument) options['storageDocumentId'] = this.storageDocumentId;
        else if (this.mode === ChatMode.AmendmentDocument) options['amendmentDocumentId'] = this.amendmentDocumentId;

        this.service.submitNewChatNote(options).subscribe({
            next: () => {
                this.closeModalWindow(true);
            },
            error: () => {
                this.closeModalWindow(false);
            }
        });
    }

    fileSelected($event) {
        $event.files.forEach((element) => {
            if (element.size > this.attachmentFileUploadRestrictions.maxFileSize) {
                this.toastyService.error(
                    this.translateService.instant(marker('Attachment size invalid')) + ' - ' + element.name
                );
                return;
            }
            if (!this.attachmentFileUploadRestrictions.allowedExtensions.some((ext) => ext == element.extension)) {
                this.toastyService.error(
                    this.translateService.instant(marker('Invalid attachment')) + ' - ' + element.name
                );
                return;
            }
            const raw = element.rawFile;
            if (raw) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.documentOnLoad(e.target.result.toString(), raw.name);
                };
                reader.readAsDataURL(element.rawFile);
            }
        });
    }

    documentOnLoad(file: string, name: string) {
        this.fileToSend = file;
        this.fileName = name;
    }

    fileRemoved($event) {
        this.fileToSend = null;
        this.fileName = null;
    }

    public closeModalWindow(reloadChatNotes: boolean) {
        this.closeModal.emit(reloadChatNotes);
    }

    private createFormGroup() {
        const form = new FormGroup({
            typeId: new FormControl(null, requiredValidator()),
            forOrganizationId: new FormControl({ value: null, disabled: true }),
            forUserId: new FormControl({ value: null, disabled: true }),
            watcherOrganizationId: new FormControl(null),
            note: new FormControl(null, requiredValidator())
        });
        form.patchValue(this as any);
        return form;
    }

    private loadAttachmentSettings(): void {
        this.attachmentService.getAttachmentSettingsForUpload().subscribe((response) => {
            this.attachmentFileUploadRestrictions = response as FileRestrictions;
        });
    }
}
